'use client';

import { useState, useTransition } from 'react';
import { Bookmark } from 'lucide-react';

import {
  bookmarkProfileAction,
  unbookmarkProfileAction,
} from '@kit/artists/server';
import { toast } from '@kit/ui/sonner';

interface SaveButtonProps {
  /** The dancer's `auth.users.id` (= `profiles.id`). Passed straight to
   * the canonical `bookmarkProfileAction`, whose resolver treats a UUID
   * as a `user_id` directly. NOT the professional_profile.id. */
  profileUserId: string;
  initialIsBookmarked: boolean;
}

/**
 * Inline mono-caption "Save" toggle for the dancer contact rail. Matches
 * the rail's editorial caption-row styling (no card chrome). Optimistic
 * with rollback on failure.
 *
 * Writes to the canonical `profile_bookmarks` table via `@kit/artists`
 * `bookmarkProfileAction` / `unbookmarkProfileAction` — the SAME table
 * the canonical "saved" lists read. (Previously called
 * `toggleBookmarkAction` from `~/lib/bookmarks/actions`, which wrote the
 * legacy polymorphic `bookmarks` table, so saved dancers never appeared
 * in the canonical list: split-brain fixed in
 * WIP_consolidating_dancer_follow_save_to_canonical_tables_2026_05_30.)
 *
 * The artists actions are split (not a toggle) and return
 * `{ success: true }` or THROW on failure, so error handling lives
 * entirely in the catch.
 */
export function SaveButton({
  profileUserId,
  initialIsBookmarked,
}: SaveButtonProps) {
  const [isBookmarked, setIsBookmarked] = useState(initialIsBookmarked);
  const [isPending, startTransition] = useTransition();

  const handleClick = () => {
    const wasBookmarked = isBookmarked;
    setIsBookmarked(!wasBookmarked);

    startTransition(async () => {
      try {
        if (wasBookmarked) {
          await unbookmarkProfileAction(profileUserId);
        } else {
          await bookmarkProfileAction(profileUserId);
        }
      } catch (error) {
        setIsBookmarked(wasBookmarked);
        toast.error(
          error instanceof Error ? error.message : 'Failed to update bookmark',
        );
      }
    });
  };

  return (
    // eslint-disable-next-line kit-components/use-kit-primitives -- editorial-register bookmark toggle (aria-pressed), public dancer-view design primitive
    <button
      type="button"
      onClick={handleClick}
      disabled={isPending}
      className="hover:text-foreground inline-flex items-center gap-2 transition-colors disabled:opacity-50"
      aria-pressed={isBookmarked}
    >
      <Bookmark className={`h-3 w-3 ${isBookmarked ? 'fill-current' : ''}`} />
      {isBookmarked ? 'Saved' : 'Save'}
    </button>
  );
}
